/**
 * Type lists and patterns — reads, and the five write functions of migration 918.
 * Every write goes through a database function that checks the person: admins
 * add and change lists; the people under a list's org unit, and admins, change
 * its types and patterns; people who may edit items set an item's type in a
 * custom list and its overrides.
 */

import { getTypedSupabaseClient } from './typedSupabaseClient';
import { getSupabaseClient } from './supabaseClient';
import { fetchHeldValues } from './itemCycleStandingService';
import type { ItemHeldValues } from './itemCycleStanding';
import { listExecutionRecipes } from './executionRecipeService';
import { parsePatternParts, resolveItemAssumptions, type ItemAssumptions } from './typePatterns';
import type {
  ItemPatternOverride,
  PatternParts,
  PatternPart,
  TypeList,
  TypeOption,
  TypePattern,
} from '../types/prioritization/typePatterns';
import type { Database } from '../types/database.generated';

type Tables = Database['public']['Tables'];
type ListRow = Tables['type_lists']['Row'];
type CustomRow = Tables['custom_types']['Row'];
type PatternRow = Tables['type_patterns']['Row'];
type OverrideRow = Tables['item_pattern_overrides']['Row'];

export function toTypeList(r: ListRow): TypeList {
  return {
    id: r.id,
    workspaceId: r.workspace_id,
    key: r.key,
    name: r.name,
    description: r.description,
    builtin: r.builtin,
    ownerOrgUnitId: r.owner_org_unit_id,
    isActive: r.is_active,
    sortOrder: r.sort_order,
  };
}

export function toCustomType(r: CustomRow): TypeOption & { listId: string } {
  return { id: r.id, listId: r.list_id, value: r.value, label: r.label, description: r.description, isActive: r.is_active };
}

export function toTypePattern(r: PatternRow): TypePattern {
  return {
    id: r.id,
    workspaceId: r.workspace_id,
    listKey: r.list_key,
    typeValue: r.type_value,
    versionNumber: r.version_number,
    supersedesId: r.supersedes_id,
    reason: r.reason,
    createdBy: r.created_by,
    createdAt: r.created_at,
    ...parsePatternParts(r),
  };
}

export function toOverride(r: OverrideRow): ItemPatternOverride {
  return {
    id: r.id,
    itemId: r.item_id,
    part: r.part as PatternPart,
    field: r.field,
    value: r.value,
    reason: r.reason,
    setBy: r.set_by,
    setAt: r.set_at,
  };
}

/** A database refusal, worded for a person. */
function failure(error: { message: string; code?: string } | null): string | null {
  if (!error) return null;
  if (error.code === '42501') return error.message || 'You do not have permission to do that.';
  return error.message;
}

export async function fetchTypeLists(workspaceId: string): Promise<TypeList[]> {
  const { data, error } = await getTypedSupabaseClient()
    .from('type_lists')
    .select('*')
    .eq('workspace_id', workspaceId)
    .order('sort_order')
    .order('name');
  if (error) throw new Error(error.message);
  return (data ?? []).map(toTypeList);
}

export async function fetchCustomTypes(workspaceId: string): Promise<Array<TypeOption & { listId: string }>> {
  const { data, error } = await getTypedSupabaseClient()
    .from('custom_types')
    .select('*')
    .eq('workspace_id', workspaceId)
    .order('sort_order')
    .order('label');
  if (error) throw new Error(error.message);
  return (data ?? []).map(toCustomType);
}

export async function fetchActivePatterns(workspaceId: string): Promise<TypePattern[]> {
  const { data, error } = await getTypedSupabaseClient()
    .from('type_patterns')
    .select('*')
    .eq('workspace_id', workspaceId)
    .eq('active', true);
  if (error) throw new Error(error.message);
  return (data ?? []).map(toTypePattern);
}

/** Every version of one type's pattern, newest first. */
export async function fetchPatternHistory(workspaceId: string, listKey: string, typeValue: string): Promise<TypePattern[]> {
  const { data, error } = await getTypedSupabaseClient()
    .from('type_patterns')
    .select('*')
    .eq('workspace_id', workspaceId)
    .eq('list_key', listKey)
    .eq('type_value', typeValue)
    .order('version_number', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []).map(toTypePattern);
}

/** An item's types in custom lists, keyed by list id. */
export async function fetchItemCustomTypes(itemId: string): Promise<Record<string, string>> {
  const { data, error } = await getTypedSupabaseClient()
    .from('item_custom_types')
    .select('list_id, value')
    .eq('item_id', itemId);
  if (error) throw new Error(error.message);
  return Object.fromEntries((data ?? []).map((r) => [r.list_id, r.value]));
}

export async function fetchItemOverrides(itemId: string): Promise<ItemPatternOverride[]> {
  const { data, error } = await getTypedSupabaseClient()
    .from('item_pattern_overrides')
    .select('*')
    .eq('item_id', itemId)
    .is('cleared_at', null);
  if (error) throw new Error(error.message);
  return (data ?? []).map(toOverride);
}

/** May the signed-in person change this list's types and patterns? (Admin, a role with type_lists.edit, or under its org unit — migration 931.) */
export async function mayEditTypeList(listId: string, userId: string): Promise<boolean> {
  const { data, error } = await getTypedSupabaseClient().rpc('internal_actor_may_edit_type_list', {
    p_actor: userId,
    p_list_id: listId,
  });
  if (error) return false;
  return data === true;
}

/**
 * Would a policy (Settings → Policies) stop the signed-in person changing this
 * list's patterns? Returns the plain reason naming the policy, or null. Asked
 * of the database — the same check revise_type_pattern makes (migration 935) —
 * so the page can leave out the button instead of refusing after the click.
 */
export async function patternPolicyBlock(workspaceId: string, listId: string): Promise<string | null> {
  const { data, error } = await getTypedSupabaseClient().rpc('check_my_act_against_policies', {
    p_workspace_id: workspaceId,
    p_scope_kind: 'type_list',
    p_scope_id: listId,
    p_action: 'type_patterns.revise',
  });
  if (error || !data || typeof data !== 'object') return null;
  const r = data as { blocked?: boolean; message?: string | null };
  return r.blocked ? (r.message ?? 'A policy holds back changes to these patterns.') : null;
}

export async function saveTypeList(input: {
  workspaceId: string;
  listId: string | null;
  name: string;
  description: string | null;
  ownerOrgUnitId: string | null;
  isActive?: boolean;
}): Promise<{ list: TypeList | null; error: string | null }> {
  const { data, error } = await getTypedSupabaseClient().rpc('save_type_list', {
    p_workspace_id: input.workspaceId,
    p_list_id: input.listId,
    p_name: input.name,
    p_description: input.description,
    p_owner_org_unit_id: input.ownerOrgUnitId,
    p_is_active: input.isActive ?? true,
  });
  return { list: data ? toTypeList(data as ListRow) : null, error: failure(error) };
}

export async function saveCustomType(input: {
  listId: string;
  typeId: string | null;
  label: string;
  description: string | null;
  isActive?: boolean;
}): Promise<{ type: (TypeOption & { listId: string }) | null; error: string | null }> {
  const { data, error } = await getTypedSupabaseClient().rpc('save_custom_type', {
    p_list_id: input.listId,
    p_type_id: input.typeId,
    p_label: input.label,
    p_description: input.description,
    p_is_active: input.isActive ?? true,
  });
  return { type: data ? toCustomType(data as CustomRow) : null, error: failure(error) };
}

export async function reviseTypePattern(input: {
  workspaceId: string;
  listKey: string;
  typeValue: string;
  parts: PatternParts;
  reason: string | null;
}): Promise<{ pattern: TypePattern | null; error: string | null }> {
  // Work types keep approach and multiplier on their own row (canon 111); never stored twice.
  const certainty =
    input.listKey === 'work_type'
      ? (({ approach: _a, effortMultiplier: _m, ...rest }) => rest)(input.parts.certainty)
      : input.parts.certainty;
  const { data, error } = await getTypedSupabaseClient().rpc('revise_type_pattern', {
    p_workspace_id: input.workspaceId,
    p_list_key: input.listKey,
    p_type_value: input.typeValue,
    p_certainty: certainty as never,
    p_shape: input.parts.shape as never,
    p_workload: input.parts.workload as never,
    p_bar: input.parts.bar as never,
    p_reason: input.reason ?? '',
  });
  return { pattern: data ? toTypePattern(data as PatternRow) : null, error: failure(error) };
}

export async function setItemCustomType(itemId: string, listId: string, value: string | null): Promise<string | null> {
  const { error } = await getTypedSupabaseClient().rpc('set_item_custom_type', {
    p_item_id: itemId,
    p_list_id: listId,
    p_value: value,
  });
  return failure(error);
}

export async function setItemPatternOverride(input: {
  itemId: string;
  part: PatternPart;
  field: string;
  /** null clears the override. */
  value: unknown;
  reason: string | null;
}): Promise<string | null> {
  const { error } = await getTypedSupabaseClient().rpc('set_item_pattern_override', {
    p_item_id: input.itemId,
    p_part: input.part,
    p_field: input.field,
    p_value: (input.value ?? null) as never,
    p_reason: input.reason,
  });
  return failure(error);
}

// ── Everything planning needs to resolve many items' assumptions at once ─────

export interface PatternContext {
  lists: TypeList[];
  patterns: TypePattern[];
  /** itemId → { listKey → value } for custom lists. */
  customByItem: Map<string, Record<string, string>>;
  /** itemId → live overrides. */
  overridesByItem: Map<string, ItemPatternOverride[]>;
  /** work type value → approach and multiplier from its own row. */
  workTypeFacts: Map<string, { approach: string | null; effortMultiplier: number | null }>;
  /**
   * itemId → the pattern versions its Plan of Record runs on (migration 938):
   * the versions in force when the plan was authorized, or one adopted since.
   * They replace the current versions for that item.
   */
  planPatternsByItem: Map<string, TypePattern[]>;
  /** itemId → what a plan in force holds (migration 1019): its types win over the live item's. */
  heldByItem?: Map<string, ItemHeldValues>;
}

export async function loadPatternContext(workspaceId: string, itemIds: string[]): Promise<PatternContext> {
  const db = getTypedSupabaseClient();
  const [lists, patterns, recipes] = await Promise.all([
    fetchTypeLists(workspaceId),
    fetchActivePatterns(workspaceId),
    listExecutionRecipes(workspaceId),
  ]);
  const keyById = new Map(lists.map((l) => [l.id, l.key]));
  const customByItem = new Map<string, Record<string, string>>();
  const overridesByItem = new Map<string, ItemPatternOverride[]>();
  if (itemIds.length > 0) {
    const [{ data: custom }, { data: overrides }] = await Promise.all([
      db.from('item_custom_types').select('item_id, list_id, value').in('item_id', itemIds),
      db.from('item_pattern_overrides').select('*').in('item_id', itemIds).is('cleared_at', null),
    ]);
    for (const r of custom ?? []) {
      const key = keyById.get(r.list_id);
      if (!key) continue;
      customByItem.set(r.item_id, { ...(customByItem.get(r.item_id) ?? {}), [key]: r.value });
    }
    for (const r of overrides ?? []) {
      const o = toOverride(r);
      overridesByItem.set(o.itemId, [...(overridesByItem.get(o.itemId) ?? []), o]);
    }
  }
  // A workspace's own version of a built-in work type replaces the global one.
  const workTypeFacts = new Map<string, { approach: string | null; effortMultiplier: number | null }>();
  for (const r of [...recipes.data].sort((a, b) => Number(a.workspace_id === workspaceId) - Number(b.workspace_id === workspaceId))) {
    workTypeFacts.set(r.work_type_ref, { approach: r.approach_kind ?? null, effortMultiplier: r.uncertainty_multiplier ?? null });
  }
  // A plan keeps the pattern versions it was authorized under (migration 938).
  const planPatternsByItem = new Map<string, TypePattern[]>();
  if (itemIds.length > 0) {
    // item_plan_pattern_versions arrives with migration 938 and is not in the generated types yet.
    const { data: pinned } = await getSupabaseClient().rpc('item_plan_pattern_versions', { p_item_ids: itemIds });
    const rows = (pinned ?? []) as Array<{ item_id: string; pattern_id: string }>;
    const known = new Map(patterns.map((p) => [p.id, p]));
    const missing = [...new Set(rows.map((r) => r.pattern_id))].filter((id) => !known.has(id));
    if (missing.length > 0) {
      const { data: older } = await db.from('type_patterns').select('*').in('id', missing);
      for (const r of older ?? []) known.set(r.id, toTypePattern(r));
    }
    for (const r of rows) {
      const pattern = known.get(r.pattern_id);
      if (pattern) planPatternsByItem.set(r.item_id, [...(planPatternsByItem.get(r.item_id) ?? []), pattern]);
    }
  }
  const heldByItem = (await fetchHeldValues(itemIds)) ?? new Map<string, ItemHeldValues>();
  return { lists, patterns, customByItem, overridesByItem, workTypeFacts, planPatternsByItem, heldByItem };
}

/** One item's assumptions from a loaded context. */
export function assumptionsFor(
  ctx: PatternContext,
  item: { id: string; work_type?: string | null; launch_type?: string | null; investment_type?: string | null },
): ItemAssumptions {
  const held = ctx.heldByItem?.get(item.id);
  return resolveItemAssumptions({
    lists: ctx.lists,
    itemTypes: {
      work_type: (held ? held.workType : item.work_type) ?? null,
      launch_type: (held ? held.launchType : item.launch_type) ?? null,
      investment_type: (held ? held.investmentType : item.investment_type) ?? null,
      ...(ctx.customByItem.get(item.id) ?? {}),
    },
    // The plan's versions come last, so they win over the current ones (938).
    patterns: [...ctx.patterns, ...(ctx.planPatternsByItem?.get(item.id) ?? [])],
    overrides: ctx.overridesByItem.get(item.id) ?? [],
    workTypeFacts: (v) => ctx.workTypeFacts.get(v) ?? null,
  });
}
